import React, { useState, useEffect, useCallback } from 'react';
import { BusinessKnowledgeContent } from '../../components/knowledge/BusinessKnowledgeContent';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { DemoFolderFilter } from '../../components/leads/DemoFolderFilter';
import {
  BookOpen, Plus, Search, FileText, Trash2, Edit3, Globe, X, Check,
  Loader2, Sparkles, ToggleLeft, ToggleRight, Bot, RefreshCw,
  FileSpreadsheet, File, Link2, Clock, ChevronRight, Eye,
  HardDrive, Upload, Copy, ExternalLink, Brain, MessageSquare,
  AlertTriangle, CheckCircle, ThumbsUp, ThumbsDown, Zap, Save
} from 'lucide-react';

// ──────── Source badge ────────
const SourceBadge = ({ type, isDark, testId }) => {
  const config = {
    manual: { label: 'Manual', icon: FileText, color: 'teal' },
    website: { label: 'Website', icon: Globe, color: 'violet' },
    google_drive: { label: 'Google Drive', icon: HardDrive, color: 'blue' },
    ai_wizard: { label: 'AI Architect', icon: Brain, color: 'teal' },
    lead_package: { label: 'Leads', icon: FileText, color: 'teal' },
  };
  const c = config[type] || config.manual;
  const Icon = c.icon;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-${c.color}-400/10 text-${c.color}-400`} data-testid={testId}>
      <Icon size={12} /> {c.label}
    </span>
  );
};

// ──────── Agent Assignment Modal ────────
const AgentAssignModal = ({ isOpen, onClose, agents, selectedIds, onSave, isDark }) => {
  const [ids, setIds] = useState(selectedIds || []);
  const bg = isDark ? 'bg-[#0c0d12]' : 'bg-white';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => { setIds(selectedIds || []); }, [selectedIds]);

  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className={`w-full max-w-md ${bg} rounded-2xl border ${border} shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className={`p-5 border-b ${border} flex items-center justify-between`}>
          <h3 className={`text-lg font-semibold ${text1}`}>Assign AI Agents</h3>
          <button onClick={onClose} className={`p-2 rounded-lg ${text2} hover:bg-white/10`}><X size={18} /></button>
        </div>
        <div className="p-5 max-h-80 overflow-y-auto space-y-2">
          {agents.length === 0 && <p className={`text-sm ${text2} text-center py-4`}>No agents found. Create one first.</p>}
          {agents.map(a => (
            <label key={a.agent_id} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${ids.includes(a.agent_id) ? 'bg-teal-400/10 border border-teal-400/30' : `${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'} border border-transparent`}`}
              data-testid={`agent-option-${a.agent_id}`}>
              <input type="checkbox" checked={ids.includes(a.agent_id)}
                onChange={() => setIds(prev => prev.includes(a.agent_id) ? prev.filter(x => x !== a.agent_id) : [...prev, a.agent_id])}
                className="sr-only" />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${ids.includes(a.agent_id) ? 'bg-teal-400 border-teal-400' : isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                {ids.includes(a.agent_id) && <Check size={12} className="text-white" />}
              </div>
              <Bot size={16} className="text-teal-400" />
              <div>
                <p className={`text-sm font-medium ${text1}`}>{a.name}</p>
                <p className={`text-xs ${text2}`}>{a.type || 'voice'}</p>
              </div>
            </label>
          ))}
        </div>
        <div className={`p-5 border-t ${border} flex gap-3`}>
          <button onClick={onClose} className={`flex-1 py-2.5 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'} ${text1} text-sm`}>Cancel</button>
          <button onClick={() => { onSave(ids); onClose(); }}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-medium" data-testid="save-agents-btn">
            Save
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ──────── Create/Edit Modal ────────
const EntryModal = ({ isOpen, onClose, entry, onSave, isDark }) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const bg = isDark ? 'bg-[#0c0d12]' : 'bg-white';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';
  const inp = isDark ? 'bg-white/5' : 'bg-gray-50';

  useEffect(() => {
    if (entry) { setName(entry.name || ''); setContent(entry.content || ''); setSourceUrl(entry.source_url || ''); }
    else { setName(''); setContent(''); setSourceUrl(''); }
  }, [entry, isOpen]);

  const handleSave = async () => {
    if (!name.trim() || !content.trim()) { toast.error('Name and content are required'); return; }
    setSaving(true);
    try { await onSave({ name, content, source_url: sourceUrl, source_type: entry?.source_type || 'manual' }); onClose(); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`w-full max-w-2xl ${bg} rounded-2xl border ${border} shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()} data-testid="kb-entry-modal">
        <div className={`p-6 border-b ${border} flex items-center justify-between sticky top-0 ${bg} z-10`}>
          <h3 className={`text-lg font-semibold ${text1}`}>{entry ? 'Edit Entry' : 'Create Entry'}</h3>
          <button onClick={onClose} data-testid="kb-entry-close" aria-label="Close entry editor" className={`p-2 rounded-lg ${text2} hover:bg-white/10`}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Pricing Info, FAQ"
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
              data-testid="kb-name-input" />
          </div>
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Content *</label>
            <textarea value={content} onChange={e => setContent(e.target.value)}
              placeholder="Enter the knowledge content your AI agent should know..." rows={10}
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-teal-400/50 resize-none`}
              data-testid="kb-content-input" />
          </div>
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Source URL (optional)</label>
            <input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="https://example.com"
              data-testid="kb-source-url-input"
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-teal-400/50`} />
          </div>
        </div>
        <div className={`p-6 border-t ${border} flex gap-3`}>
          <button onClick={onClose} data-testid="kb-entry-cancel" className={`flex-1 py-3 rounded-xl ${inp} border ${border} ${text1} hover:bg-white/10`}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] font-medium disabled:opacity-50"
            data-testid="save-kb-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {entry ? 'Update' : 'Create'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ──────── Website Import Modal ────────
const WebsiteImportModal = ({ isOpen, onClose, onImport, isDark }) => {
  const [url, setUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const bg = isDark ? 'bg-[#0c0d12]' : 'bg-white';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';
  const inp = isDark ? 'bg-white/5' : 'bg-gray-50';

  const handleImport = async () => {
    if (!url.trim()) { toast.error('Enter a URL'); return; }
    setImporting(true);
    try { await onImport(url); setUrl(''); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`w-full max-w-lg ${bg} rounded-2xl border ${border} shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className={`p-6 border-b ${border} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400/20 to-teal-400/20 flex items-center justify-center">
              <Globe className="text-violet-400" size={20} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${text1}`}>Import from Website</h3>
              <p className={`text-sm ${text2}`}>AI creates one comprehensive knowledge base</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${text2} hover:bg-white/10`}><X size={18} /></button>
        </div>
        <div className="p-6">
          <label className={`block text-sm ${text2} mb-2`}>Website URL</label>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com"
            className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-teal-400/50 mb-2`}
            data-testid="website-url-input" />
          <p className={`text-xs ${text2} mb-6`}>We'll scrape the page and use AI to create a single, detailed summary including FAQs, pricing, services, and all key business details.</p>
          <button onClick={handleImport} disabled={importing || !url.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-400 to-teal-500 text-white font-medium disabled:opacity-50"
            data-testid="import-website-btn">
            {importing ? <><Loader2 size={16} className="animate-spin" /> Analyzing website...</> : <><Sparkles size={16} /> Import & Summarize</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ──────── Google Drive Modal ────────
const GoogleDriveModal = ({ isOpen, onClose, onImportLink, isDark }) => {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [syncInterval, setSyncInterval] = useState(30);
  const [importing, setImporting] = useState(false);
  const bg = isDark ? 'bg-[#0c0d12]' : 'bg-white';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';
  const inp = isDark ? 'bg-white/5' : 'bg-gray-50';

  const detectType = (url) => {
    if (url.includes('/spreadsheets/')) return { type: 'Google Sheet', icon: <FileSpreadsheet size={16} className="text-green-400" /> };
    if (url.includes('/document/')) return { type: 'Google Doc', icon: <FileText size={16} className="text-blue-400" /> };
    if (url.includes('/presentation/')) return { type: 'Google Slides', icon: <File size={16} className="text-amber-400" /> };
    if (url.includes('docs.google.com') || url.includes('drive.google.com')) return { type: 'Google File', icon: <HardDrive size={16} className="text-blue-400" /> };
    return null;
  };

  const detected = url.trim() ? detectType(url) : null;

  const handleImport = async () => {
    if (!url.trim()) { toast.error('Paste a Google link'); return; }
    setImporting(true);
    try {
      await onImportLink(url, name, syncInterval);
      setUrl(''); setName(''); setSyncInterval(30);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed. Make sure the file is shared as "Anyone with the link".');
    }
    finally { setImporting(false); }
  };

  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`w-full max-w-lg ${bg} rounded-2xl border ${border} shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className={`p-6 border-b ${border} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400/20 to-green-400/20 flex items-center justify-center">
              <HardDrive className="text-blue-400" size={20} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${text1}`}>Import from Google</h3>
              <p className={`text-sm ${text2}`}>Paste a Google Sheets, Docs, or Slides link</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${text2} hover:bg-white/10`}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Google Link *</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..."
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-blue-400/50`}
              data-testid="google-link-input" />
            {detected && (
              <div className="flex items-center gap-2 mt-2">
                {detected.icon}
                <span className={`text-xs ${text2}`}>Detected: {detected.type}</span>
              </div>
            )}
          </div>
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Name (optional)</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Product Pricing Sheet"
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} placeholder-gray-500 focus:outline-none focus:border-blue-400/50`}
              data-testid="google-name-input" />
          </div>
          <div>
            <label className={`block text-sm ${text2} mb-2`}>Auto-Sync Interval</label>
            <select value={syncInterval} onChange={e => setSyncInterval(Number(e.target.value))}
              className={`w-full px-4 py-3 rounded-xl ${inp} border ${border} ${text1} focus:outline-none focus:border-blue-400/50`}
              data-testid="sync-interval-select">
              <option value={0}>Manual only (no auto-sync)</option>
              <option value={15}>Every 15 minutes</option>
              <option value={30}>Every 30 minutes</option>
              <option value={60}>Every hour</option>
              <option value={360}>Every 6 hours</option>
              <option value={1440}>Every 24 hours</option>
            </select>
            <p className={`text-xs ${text2} mt-1`}>
              {syncInterval > 0 
                ? `AI will automatically pull the latest data every ${syncInterval < 60 ? `${syncInterval} min` : `${syncInterval / 60}h`}` 
                : 'Use the sync button to manually refresh data'}
            </p>
          </div>
          <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-400/5 border border-blue-400/10' : 'bg-blue-50 border border-blue-100'}`}>
            <p className={`text-xs ${text2}`}>
              <strong>Tip:</strong> Make sure your file is shared as <strong>"Anyone with the link"</strong> so we can read its content. When you update the sheet or doc, the AI will automatically pick up the changes on the next sync.
            </p>
          </div>
          <button onClick={handleImport} disabled={importing || !url.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-400 to-teal-500 text-white font-medium disabled:opacity-50 hover:shadow-lg transition-all"
            data-testid="import-google-btn">
            {importing ? <><Loader2 size={16} className="animate-spin" /> Importing...</> : <><Upload size={16} /> Import</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ──────── Content Preview Modal ────────
const PreviewModal = ({ isOpen, onClose, entry, isDark }) => {
  const bg = isDark ? 'bg-[#0c0d12]' : 'bg-white';
  const border = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const text1 = isDark ? 'text-white' : 'text-gray-900';
  const text2 = isDark ? 'text-gray-400' : 'text-gray-500';

  if (!isOpen || !entry) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`w-full min-w-0 max-w-3xl ${bg} rounded-2xl border ${border} shadow-2xl max-h-[85vh] flex flex-col`} onClick={e => e.stopPropagation()} data-testid="kb-preview-modal" role="dialog" aria-modal="true" aria-labelledby="kb-preview-title">
        <div className={`p-5 border-b ${border} flex items-center justify-between shrink-0`}>
          <div className="min-w-0 [overflow-wrap:anywhere]">
            <h3 id="kb-preview-title" data-testid="kb-preview-title" className={`text-lg font-semibold ${text1}`}>{entry.name}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <SourceBadge type={entry.source_type} isDark={isDark} testId="kb-preview-source-badge" />
              {/^https?:\/\//i.test(entry.source_url || '') && <a href={entry.source_url} title={entry.source_url} target="_blank" rel="noreferrer" data-testid="kb-preview-source-url" className={`min-w-0 max-w-full text-xs ${text2} flex items-center gap-1 hover:text-teal-400`}><ExternalLink size={10} className="shrink-0" /><span className="truncate">{entry.source_url.replace(/^https?:\/\//i, '').split('/')[0]}</span></a>}
            </div>
          </div>
          <button onClick={onClose} data-testid="kb-preview-close" aria-label="Close knowledge preview" className={`p-2 rounded-lg shrink-0 ${text2} hover:bg-white/10`}><X size={18} /></button>
        </div>
        <div className="flex-1 min-w-0 overflow-y-auto p-5 sm:p-7" data-testid="kb-preview-scroll">
          {entry.knowledge_format_version === 'business-reference-v2'
            ? <BusinessKnowledgeContent content={entry.content} references={entry.source_references || []} isDark={isDark} />
            : <pre data-testid="kb-preview-legacy-content" className={`text-sm ${text1} whitespace-pre-wrap font-sans leading-relaxed [overflow-wrap:anywhere]`}>{entry.content}</pre>}
        </div>
        <div className={`p-4 border-t ${border} flex gap-2 shrink-0`}>
          <button onClick={() => { navigator.clipboard.writeText(entry.content); toast.success('Copied'); }}
            data-testid="kb-preview-copy"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'} ${text1} text-sm hover:bg-white/10`}>
            <Copy size={14} /> Copy
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ──────── AI Training Tab Component ────────
const QUALITY_CONFIG = {
  good: { color: 'text-green-400', bg: 'bg-green-400/10', icon: CheckCircle, label: 'Good' },
  needs_improvement: { color: 'text-amber-400', bg: 'bg-amber-400/10', icon: AlertTriangle, label: 'Needs Work' },
  poor: { color: 'text-red-400', bg: 'bg-red-400/10', icon: ThumbsDown, label: 'Poor' },
};

const CATEGORY_COLORS = {
  pricing: 'bg-violet-400/10 text-violet-400',
  scheduling: 'bg-blue-400/10 text-blue-400',
  objection: 'bg-red-400/10 text-red-400',
  information: 'bg-teal-400/10 text-teal-400',
  qualification: 'bg-amber-400/10 text-amber-400',
  closing: 'bg-green-400/10 text-green-400',
  general: 'bg-gray-400/10 text-gray-400',
};

const AITrainingTab = ({ subAccountId, isDark, api, agents }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editResponse, setEditResponse] = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(null);
  const [editAgentsFor, setEditAgentsFor] = useState(null);
  const [pickedAgentIds, setPickedAgentIds] = useState([]);

  const cardBg = isDark ? 'bg-[#0c0d12]/80' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.04]' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-white/[0.03]' : 'bg-gray-50';

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/sub-accounts/${subAccountId}/training?status=${filter}`);
      setEntries(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, subAccountId, filter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSaveTraining = async (entry) => {
    setSaving(entry.training_id);
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/training/${entry.training_id}`, {
        trained_response: editResponse,
      });
      toast.success('Training saved and synced to Knowledge Base');
      setEditingEntry(null);
      setEditResponse('');
      fetchEntries();
    } catch (e) { toast.error('Failed to save'); }
    setSaving(null);
  };

  const handleDismiss = async (entry) => {
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/training/${entry.training_id}`);
      setEntries(prev => prev.filter(e => e.training_id !== entry.training_id));
      toast.success('Entry removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  const handleSaveAgents = async (entry) => {
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/training/${entry.training_id}`, { agent_ids: pickedAgentIds });
      setEditAgentsFor(null);
      fetchEntries();
      toast.success('Agent access updated');
    } catch (e) { toast.error('Failed to update'); }
  };

  const trained = entries.filter(e => e.status === 'trained');
  const pending = entries.filter(e => e.status === 'pending');

  return (
    <div data-testid="ai-training-tab">
      {/* Stats + Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${cardBg} border ${cardBorder}`}>
          <Brain size={13} className="text-violet-400" />
          <span className={`text-xs ${textSecondary}`}>{trained.length} trained</span>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${cardBg} border ${cardBorder}`}>
          <Zap size={13} className="text-amber-400" />
          <span className={`text-xs ${textSecondary}`}>{pending.length} pending</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center rounded-lg overflow-hidden border" style={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#e5e7eb' }}>
          {['all', 'pending', 'trained'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium transition ${filter === f
                ? 'bg-teal-400/10 text-teal-400'
                : `${isDark ? 'bg-white/[0.02] text-gray-500' : 'bg-gray-50 text-gray-400'} hover:text-teal-400`}`}
              data-testid={`filter-${f}`}
            >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
      </div>

      {/* Entries */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-teal-400" size={22} />
        </div>
      ) : entries.length === 0 ? (
        <div className={`text-center py-14 ${cardBg} rounded-2xl border ${cardBorder}`}>
          <Brain size={32} className={`mx-auto mb-2 ${textSecondary}`} />
          <h3 className={`text-base font-medium ${textPrimary} mb-1`}>No training data yet</h3>
          <p className={`text-sm ${textSecondary}`}>Analyze contacts from their timeline to generate training patterns</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(entry => {
            const qc = QUALITY_CONFIG[entry.quality] || QUALITY_CONFIG.needs_improvement;
            const catClass = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.general;
            const isEditing = editingEntry?.training_id === entry.training_id;

            return (
              <div key={entry.training_id}
                className={`${cardBg} rounded-xl border ${cardBorder} p-4 transition-all hover:border-white/[0.08]`}
                data-testid={`training-entry-${entry.training_id}`}>
                {/* Compact header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${entry.status === 'trained' ? 'bg-green-400' : qc.color.replace('text-', 'bg-')}`} />
                  <span className={`text-sm font-medium ${textPrimary} flex-1`}>{entry.question}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${catClass}`}>{entry.category}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${qc.bg} ${qc.color}`}>{qc.label}</span>
                  <span className={`text-[10px] ${textSecondary}`}>{entry.frequency}x</span>
                </div>

                {/* Current response (compact) */}
                <p className={`text-xs ${textSecondary} mb-2 line-clamp-2`}>{entry.current_response}</p>

                {/* Trained response */}
                {entry.trained_response && !isEditing && (
                  <div className="rounded-lg p-2.5 mb-2 bg-green-400/5 border border-green-400/10">
                    <p className="text-[10px] font-medium text-green-400 mb-0.5">Trained:</p>
                    <p className={`text-xs ${textPrimary}`}>{entry.trained_response}</p>
                  </div>
                )}

                {/* Suggestion (only if not trained yet) */}
                {entry.suggestion && !entry.trained_response && !isEditing && entry.suggestion !== 'No change needed' && (
                  <p className={`text-xs text-amber-400/80 mb-2`}>Suggestion: {entry.suggestion}</p>
                )}

                {/* Edit form */}
                {isEditing && (
                  <div className="mt-2">
                    <textarea value={editResponse} onChange={e => setEditResponse(e.target.value)} rows={2}
                      className={`w-full rounded-lg border ${cardBorder} ${inputBg} ${textPrimary} p-2.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400/50 resize-none`}
                      placeholder="Write the ideal response..." data-testid={`training-input-${entry.training_id}`} />
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => handleSaveTraining(entry)} disabled={!editResponse.trim() || saving === entry.training_id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-500 text-white text-xs font-medium hover:bg-teal-600 disabled:opacity-50 transition"
                        data-testid={`save-training-${entry.training_id}`}>
                        {saving === entry.training_id ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                      </button>
                      <button onClick={() => { setEditingEntry(null); setEditResponse(''); }}
                        className={`px-3 py-1.5 rounded-lg text-xs ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Footer: agents + actions */}
                <div className="mt-2 pt-2 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.03)' : '#f3f4f6' }}>
                  {editAgentsFor === entry.training_id ? (
                    <div className="mb-2">
                      <p className={`text-[10px] uppercase tracking-wider ${textSecondary} mb-1.5`}>Select agents with access</p>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {agents.map(a => {
                          const sel = pickedAgentIds.includes(a.agent_id);
                          return (
                            <button key={a.agent_id}
                              onClick={() => setPickedAgentIds(prev => sel ? prev.filter(id => id !== a.agent_id) : [...prev, a.agent_id])}
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border transition ${sel
                                ? 'bg-teal-400/10 border-teal-400/30 text-teal-400'
                                : `${isDark ? 'bg-white/[0.02] border-white/[0.06] text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'} hover:border-teal-400/20`
                              }`}
                              data-testid={`pick-agent-${a.agent_id}-${entry.training_id}`}>
                              <Bot size={10} /> {a.name}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveAgents(entry)}
                          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-teal-500 text-white text-[11px] font-medium hover:bg-teal-600 transition"
                          data-testid={`save-agents-${entry.training_id}`}>
                          <Save size={10} /> Save
                        </button>
                        <button onClick={() => setEditAgentsFor(null)}
                          className={`px-3 py-1 rounded-lg text-[11px] ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <button onClick={() => { setEditAgentsFor(entry.training_id); setPickedAgentIds(entry.kb_agent_ids || entry.agent_ids || []); }}
                        className="flex items-center gap-1.5 hover:bg-white/[0.03] rounded-lg p-1 -m-1 transition group"
                        data-testid={`manage-agents-${entry.training_id}`}>
                        <Bot size={11} className="text-teal-400" />
                        <div className="flex flex-wrap gap-1">
                          {agents.filter(a => (entry.kb_agent_ids || entry.agent_ids || []).includes(a.agent_id)).map(a => (
                            <span key={a.agent_id} className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-white/[0.04] text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{a.name}</span>
                          ))}
                          {(!(entry.kb_agent_ids || entry.agent_ids) || (entry.kb_agent_ids || entry.agent_ids || []).length === 0) && (
                            <span className={`text-[10px] ${textSecondary}`}>No agents</span>
                          )}
                        </div>
                        <Edit3 size={10} className={`${textSecondary} opacity-0 group-hover:opacity-100 transition`} />
                      </button>
                      <div className="flex items-center gap-1">
                        {!isEditing && (
                          <button onClick={() => { setEditingEntry(entry); setEditResponse(entry.trained_response || entry.suggestion || ''); }}
                            className="p-1 rounded-lg hover:bg-teal-400/10 text-teal-400 transition" title="Edit response"
                            data-testid={`train-btn-${entry.training_id}`}>
                            <Edit3 size={12} />
                          </button>
                        )}
                        <button onClick={() => handleDismiss(entry)}
                          className="p-1 rounded-lg hover:bg-red-400/10 text-red-400/60 hover:text-red-400 transition" title="Remove"
                          data-testid={`dismiss-btn-${entry.training_id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};



// ──────── Main Content ────────
const KnowledgeBaseContent = () => {
  const { subAccountId } = useParams();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();

  const [entries, setEntries] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showDriveModal, setShowDriveModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [agentAssignEntry, setAgentAssignEntry] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [syncingIds, setSyncingIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('kb');

  const isDark = isDarkMode;
  const cardBg = isDark ? 'bg-[#0c0d12]/90' : 'bg-white/90';
  const cardBorder = isDark ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDark ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kbRes, agRes] = await Promise.all([
        api.get(`/api/sub-accounts/${subAccountId}/knowledge-base`),
        api.get(`/api/sub-accounts/${subAccountId}/agents`),
      ]);
      setEntries(kbRes.data || []);
      setAgents(agRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, subAccountId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveEntry = async (data) => {
    if (editingEntry) {
      await api.put(`/api/sub-accounts/${subAccountId}/knowledge-base/${editingEntry.kb_id}`, data);
      toast.success('Entry updated');
    } else {
      await api.post(`/api/sub-accounts/${subAccountId}/knowledge-base`, data);
      toast.success('Entry created');
    }
    setEditingEntry(null);
    fetchAll();
  };

  const handleToggle = async (entry) => {
    try {
      const res = await api.put(`/api/sub-accounts/${subAccountId}/knowledge-base/${entry.kb_id}/toggle`);
      setEntries(prev => prev.map(e => e.kb_id === entry.kb_id ? { ...e, enabled: res.data.enabled } : e));
      toast.success(res.data.enabled ? 'Activated' : 'Deactivated');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (entry) => {
    setDeleting(true);
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/knowledge-base/${entry.kb_id}`);
      setEntries(prev => prev.filter(e => e.kb_id !== entry.kb_id));
      toast.success(`"${entry.name}" deleted`);
      setDeleteConfirm(null);
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleWebsiteImport = async (url) => {
    const res = await api.post(`/api/sub-accounts/${subAccountId}/knowledge-base/website-import`, { url });
    toast.success('Website imported as knowledge base');
    fetchAll();
  };

  const handleDriveImport = async (url, name, syncInterval) => {
    const res = await api.post(`/api/sub-accounts/${subAccountId}/knowledge-base/google-import`, { url, name, sync_interval: syncInterval || 0 });
    toast.success(`Imported "${res.data.name}" — ${syncInterval > 0 ? `auto-syncs every ${syncInterval < 60 ? syncInterval + 'min' : (syncInterval/60) + 'h'}` : 'manual sync'}`);
    fetchAll();
  };

  const handleSync = async (entry) => {
    setSyncingIds(prev => [...prev, entry.kb_id]);
    try {
      const { data } = await api.post(`/api/sub-accounts/${subAccountId}/knowledge-base/${entry.kb_id}/sync`);
      if (data?.success === false) {
        toast.error(data?.message || 'Sync failed');
      } else {
        toast.success(data?.message || 'Synced');
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || 'Sync failed');
    }
    finally { setSyncingIds(prev => prev.filter(id => id !== entry.kb_id)); }
  };

  const handleAssignAgents = async (agentIds) => {
    if (!agentAssignEntry) return;
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/knowledge-base/${agentAssignEntry.kb_id}/agents`, { agent_ids: agentIds });
      setEntries(prev => prev.map(e => e.kb_id === agentAssignEntry.kb_id ? { ...e, agent_ids: agentIds } : e));
      toast.success('Agents assigned');
    } catch { toast.error('Failed'); }
    setAgentAssignEntry(null);
  };

  const getAgentNames = (agentIds) => {
    if (!agentIds?.length) return 'None';
    return agentIds.map(id => agents.find(a => a.agent_id === id)?.name || id).join(', ');
  };

  const [demoFolder, setDemoFolder] = useState('production');
  const filtered = entries.filter(e => {
    const matchSearch = !search || e.name?.toLowerCase().includes(search.toLowerCase()) || e.content?.toLowerCase().includes(search.toLowerCase());
    const matchSource = sourceFilter === 'all' || e.source_type === sourceFilter;
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' && e.enabled !== false) || (statusFilter === 'inactive' && e.enabled === false);
    return matchSearch && matchSource && matchStatus && (demoFolder === 'all' || (demoFolder === 'demo' ? e.demo_only : !e.demo_only));
  });

  const stats = {
    total: entries.length,
    active: entries.filter(e => e.enabled !== false).length,
    website: entries.filter(e => e.source_type === 'website').length,
    drive: entries.filter(e => e.source_type === 'google_drive').length,
  };

  if (loading) {
    return <div className={`min-h-screen ${bgMain} flex items-center justify-center`}><div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" /></div>;
  }

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="knowledge-base-page">
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-teal-400/5 rounded-full blur-[100px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-semibold ${textPrimary}`}>Knowledge Base</h1>
            <p className={textSecondary}>Manage knowledge sources and AI training for your agents</p>
          </div>
          {activeTab === 'kb' && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setShowDriveModal(true)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition text-sm`}
                data-testid="google-drive-btn">
                <HardDrive size={16} /> Google Import
              </button>
              <button onClick={() => setShowWebsiteModal(true)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10 transition text-sm`}
                data-testid="import-website-header-btn">
                <Globe size={16} /> Import Website
              </button>
              <button onClick={() => { setEditingEntry(null); setShowEntryModal(true); }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-teal-400/25 transition-all"
                data-testid="create-kb-btn">
                <Plus size={16} /> Create Entry
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className={`flex items-center gap-1 mb-6 p-1 rounded-xl ${isDark ? 'bg-white/[0.03]' : 'bg-gray-100'} w-fit`} data-testid="kb-tabs">
          <button onClick={() => setActiveTab('kb')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'kb'
              ? `${isDark ? 'bg-white/[0.08] text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'}`
              : `${textSecondary} hover:text-white`}`}
            data-testid="tab-knowledge-base">
            <BookOpen size={15} /> Knowledge Base
          </button>
          <button onClick={() => setActiveTab('training')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'training'
              ? 'bg-gradient-to-r from-violet-500/20 to-teal-500/20 text-teal-400 shadow-sm'
              : `${textSecondary} hover:text-teal-400`}`}
            data-testid="tab-ai-training">
            <Brain size={15} /> AI Training
          </button>
        </div>

        {/* AI Training Tab */}
        {activeTab === 'training' && (
          <AITrainingTab subAccountId={subAccountId} isDark={isDark} api={api} agents={agents} />
        )}

        {/* Knowledge Base Tab */}
        {activeTab === 'kb' && <>
        <DemoFolderFilter value={demoFolder} onChange={setDemoFolder} scope="kb"/>

        {/* Stats */}
        {entries.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Entries', value: stats.total, icon: BookOpen, color: 'teal' },
              { label: 'Active', value: stats.active, icon: Check, color: 'green' },
              { label: 'From Websites', value: stats.website, icon: Globe, color: 'violet' },
              { label: 'From Drive', value: stats.drive, icon: HardDrive, color: 'blue' },
            ].map(s => (
              <div key={s.label} className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4`}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`w-9 h-9 rounded-lg bg-${s.color}-400/10 flex items-center justify-center`}>
                    <s.icon className={`text-${s.color}-400`} size={18} />
                  </div>
                  <span className={`text-xs ${textSecondary}`}>{s.label}</span>
                </div>
                <p className={`text-xl font-bold ${textPrimary}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        {entries.length > 0 && (
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4 mb-6`}>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1 relative">
                <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search knowledge base..."
                  className={`w-full pl-9 pr-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                  data-testid="kb-search-input" />
              </div>
              <div className="flex gap-1.5">
                {[
                  { key: 'all', label: `All (${entries.length})` },
                  { key: 'active', label: `Active (${stats.active})` },
                  { key: 'inactive', label: `Inactive (${entries.length - stats.active})` },
                ].map(f => (
                  <button key={f.key} onClick={() => setStatusFilter(f.key)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${statusFilter === f.key ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' : `${inputBg} ${textSecondary} border ${cardBorder} hover:bg-white/10`}`}
                    data-testid={`kb-filter-${f.key}`}>{f.label}</button>
                ))}
              </div>
              <div className="flex gap-1.5">
                {[
                  { key: 'all', label: 'All Sources' },
                  { key: 'manual', label: 'Manual' },
                  { key: 'website', label: 'Website' },
                  { key: 'google_drive', label: 'Drive' },
                ].map(f => (
                  <button key={f.key} onClick={() => setSourceFilter(f.key)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${sourceFilter === f.key ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30' : `${inputBg} ${textSecondary} border ${cardBorder} hover:bg-white/10`}`}
                    data-testid={`kb-source-${f.key}`}>{f.label}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {entries.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={`${cardBg} backdrop-blur-xl border ${cardBorder} rounded-2xl p-16 text-center`}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className={isDark ? 'text-gray-600' : 'text-gray-400'} size={32} />
            </div>
            <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>No Knowledge Base Entries</h3>
            <p className={`${textSecondary} text-sm max-w-md mx-auto mb-6`}>
              Create entries manually, import from a website, or connect Google Drive to give your AI agents the knowledge they need.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => setShowDriveModal(true)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10`}>
                <HardDrive size={16} /> Google Import
              </button>
              <button onClick={() => setShowWebsiteModal(true)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10`}>
                <Globe size={16} /> Import Website
              </button>
              <button onClick={() => { setEditingEntry(null); setShowEntryModal(true); }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white font-medium">
                <FileText size={16} /> Create Entry
              </button>
            </div>
          </motion.div>
        )}

        {/* Table */}
        {filtered.length > 0 && (
          <div className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full block xl:table [&_td]:min-w-0" data-testid="knowledge-entry-table">
                <thead className="hidden xl:table-header-group">
                  <tr className={`border-b ${cardBorder} ${isDark ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    {['Name', 'Source', 'AI Agents', 'Added', 'Sync', 'Status', ''].map(h => (
                      <th key={h} className={`px-5 py-3 text-left text-[11px] font-medium ${textSecondary} uppercase tracking-wider`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block xl:table-row-group">
                  {filtered.map(entry => {
                    const agentNames = getAgentNames(entry.agent_ids);
                    const isEnabled = entry.enabled !== false;
                    const isSyncing = syncingIds.includes(entry.kb_id);
                    return (
                      <tr key={entry.kb_id} className={`grid grid-cols-2 xl:table-row border-b ${cardBorder} hover:bg-white/[0.03] transition-colors group`}
                        data-testid={`kb-row-${entry.kb_id}`}>
                        {/* Name */}
                        <td className="min-w-0 col-span-2 px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isEnabled ? 'bg-gradient-to-br from-teal-400/10 to-violet-400/10' : isDark ? 'bg-white/[0.03]' : 'bg-gray-100'}`}>
                              <FileText className={`w-4 h-4 ${isEnabled ? 'text-teal-400' : 'text-gray-500'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium ${textPrimary} truncate max-w-[250px]`}>{entry.name}</p>
                              <p className={`text-xs ${textSecondary} truncate max-w-[250px]`}>
                                {entry.content?.substring(0, 60)}...
                              </p>
                            </div>
                          </div>
                        </td>
                        {/* Source */}
                        <td className="px-5 py-3.5">
                          <SourceBadge type={entry.source_type || 'manual'} isDark={isDark} testId={`kb-source-badge-${entry.kb_id}`} />
                        </td>
                        {/* Agents */}
                        <td className="px-5 py-3.5">
                          <button onClick={() => { setAgentAssignEntry(entry); setShowAgentModal(true); }}
                            className={`flex min-w-0 max-w-full items-center gap-1.5 text-sm ${textSecondary} hover:text-teal-400 transition`}
                            data-testid={`assign-agents-${entry.kb_id}`}>
                            <Bot size={14} className="text-teal-400 shrink-0" />
                            <span className="truncate max-w-[150px]">{agentNames}</span>
                          </button>
                        </td>
                        {/* Added */}
                        <td className="px-5 py-3.5">
                          <span className={`text-xs ${textSecondary}`} data-testid={`kb-added-${entry.kb_id}`}>
                            {entry.created_at ? new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </span>
                        </td>
                        {/* Sync */}
                        <td className="px-5 py-3.5">
                          {entry.source_type !== 'manual' ? (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleSync(entry)} disabled={isSyncing}
                                className={`p-1.5 rounded-lg transition ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-100'} disabled:opacity-50`}
                                title="Sync now" data-testid={`sync-${entry.kb_id}`}>
                                <RefreshCw size={14} className={`${isSyncing ? 'animate-spin text-teal-400' : textSecondary}`} />
                              </button>
                              <div className="flex flex-col">
                                {entry.sync_interval > 0 && (
                                  <span className={`text-xs text-teal-400`}>
                                    {entry.sync_interval < 60 ? `${entry.sync_interval}m` : `${entry.sync_interval / 60}h`}
                                  </span>
                                )}
                                {entry.last_synced_at && (
                                  <span className={`text-[10px] ${textSecondary}`}>
                                    {new Date(entry.last_synced_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className={`text-xs ${textSecondary}`}>-</span>
                          )}
                        </td>
                        {/* Status */}
                        <td className="px-5 py-3.5">
                          <button onClick={() => handleToggle(entry)} data-testid={`toggle-kb-${entry.kb_id}`}>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-all ${isEnabled ? 'bg-green-400/10 text-green-400 hover:bg-green-400/20' : isDark ? 'bg-white/[0.03] text-gray-500 hover:bg-white/[0.06]' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                              {isEnabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                              {isEnabled ? 'Active' : 'Inactive'}
                            </span>
                          </button>
                        </td>
                        {/* Actions */}
                        <td className="min-w-0 col-span-2 px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setShowPreview(entry)}
                              className={`p-1.5 rounded-lg transition ${isDark ? 'hover:bg-white/[0.06] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                              title="Preview" data-testid={`preview-kb-${entry.kb_id}`}>
                              <Eye size={15} />
                            </button>
                            <button onClick={() => { setEditingEntry(entry); setShowEntryModal(true); }}
                              className={`p-1.5 rounded-lg transition ${isDark ? 'hover:bg-white/[0.06] text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                              title="Edit" data-testid={`edit-kb-${entry.kb_id}`}>
                              <Edit3 size={15} />
                            </button>
                            <button onClick={() => setDeleteConfirm(entry)}
                              className="p-1.5 rounded-lg transition hover:bg-red-500/10 text-red-400" title="Delete"
                              data-testid={`delete-kb-${entry.kb_id}`}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </>}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showEntryModal && <EntryModal isOpen={showEntryModal} onClose={() => { setShowEntryModal(false); setEditingEntry(null); }} entry={editingEntry} onSave={handleSaveEntry} isDark={isDark} />}
        {showWebsiteModal && <WebsiteImportModal isOpen={showWebsiteModal} onClose={() => setShowWebsiteModal(false)} onImport={handleWebsiteImport} isDark={isDark} />}
        {showDriveModal && <GoogleDriveModal isOpen={showDriveModal} onClose={() => setShowDriveModal(false)} onImportLink={handleDriveImport} isDark={isDark} />}
        {showAgentModal && <AgentAssignModal isOpen={showAgentModal} onClose={() => { setShowAgentModal(false); setAgentAssignEntry(null); }} agents={agents} selectedIds={agentAssignEntry?.agent_ids || []} onSave={handleAssignAgents} isDark={isDark} />}
        {showPreview && <PreviewModal isOpen={!!showPreview} onClose={() => setShowPreview(null)} entry={showPreview} isDark={isDark} />}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { if (!deleting) setDeleteConfirm(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className={`w-full max-w-sm ${isDark ? 'bg-[#0c0d12]' : 'bg-white'} rounded-2xl border ${cardBorder} shadow-2xl overflow-hidden`}
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={22} className="text-red-400" />
                </div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-1`}>Delete Knowledge Base</h3>
                <p className={`text-sm ${textSecondary} mb-1`}>Are you sure you want to delete</p>
                <p className={`text-sm font-medium ${textPrimary} mb-4`}>"{deleteConfirm.name}"?</p>
                <p className={`text-xs ${textSecondary}`}>This action cannot be undone.</p>
              </div>
              <div className={`px-6 pb-6 flex gap-3`}>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className={`flex-1 py-2.5 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} text-sm font-medium transition-colors`}
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? <><Loader2 size={14} className="animate-spin" /> Deleting...</> : <><Trash2 size={14} /> Delete</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function KnowledgeBasePage() {
  return (
    <SubAccountLayout>
      <KnowledgeBaseContent />
    </SubAccountLayout>
  );
}
